import * as React from "react"
import { AlertTriangle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { i18n } from "@/lib/i18n"

type ErrorBoundaryProps = {
  children: React.ReactNode
  /** Shown in dev console when an error is caught */
  name?: string
  /** When set, shown instead of the default retry UI */
  fallback?: React.ReactNode
}

type ErrorBoundaryState = {
  hasError: boolean
}

/**
 * Catches render errors in children so a failed CMS or UI subtree does not blank the whole landing page.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const label = this.props.name ? `:${this.props.name}` : ""
    // #region agent log
    fetch('http://127.0.0.1:7298/ingest/b70b6667-0539-40c6-b70a-90c33d5e52ea',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'7dc830'},body:JSON.stringify({sessionId:'7dc830',runId:'pre-fix',hypothesisId:'H5',location:'error-boundary.tsx:31',message:'ErrorBoundary caught error',data:{name:this.props.name ?? null,error:error?.message ?? String(error),stack:typeof error?.stack === 'string'?error.stack.slice(0,2000):null,componentStack:info?.componentStack?.slice(0,2000) ?? null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log
    if (import.meta.env.DEV) {
      console.error(`[ErrorBoundary${label}]`, error, info.componentStack)
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }
      return (
        <div className="rounded-lg border border-border/60 bg-muted/40">
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
            <AlertTriangle className="mb-1 h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">{i18n.t("errors.boundary.title")}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button type="button" size="sm" onClick={() => window.location.reload()}>
                {i18n.t("errors.boundary.reload")}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={this.handleRetry}>
                {i18n.t("errors.boundary.tryAgain")}
              </Button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
